import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { Add, Edit, Delete, Refresh } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import type { RootState } from '../../store';
import {
  fetchGroups,
  createGroupThunk,
  updateGroupThunk,
  deleteGroupThunk,
  clearGroupError,
  clearGroupMessage,
} from '../../store/slices/adminClientGroupSlice';
import { ClientGroupDetail, ClientGroupManageRequest } from '../../types/adminClientGroup';
import ClientGroupFormDialog from './ClientGroupFormDialog';

const ClientGroupManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { adminApiKey } = useAppSelector((state: RootState) => state.adminAuth);
  const { groups, loading, saving, error, actionMessage } = useAppSelector(
    (state: RootState) => state.adminClientGroups
  );

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingGroup, setEditingGroup] = useState<ClientGroupDetail | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ClientGroupDetail | null>(null);

  useEffect(() => {
    if (adminApiKey) {
      dispatch(fetchGroups(adminApiKey));
    }
  }, [adminApiKey, dispatch]);

  const handleRefresh = () => {
    if (adminApiKey) {
      dispatch(fetchGroups(adminApiKey));
    }
  };

  const handleOpenCreate = () => {
    setFormMode('create');
    setEditingGroup(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (group: ClientGroupDetail) => {
    setFormMode('edit');
    setEditingGroup(group);
    setFormOpen(true);
  };

  const handleCreate = async (payload: ClientGroupManageRequest) => {
    if (!adminApiKey) return;
    const result = await dispatch(createGroupThunk({ payload, adminApiKey }));
    if (createGroupThunk.fulfilled.match(result)) {
      setFormOpen(false);
    }
  };

  const handleUpdate = async (id: number, payload: ClientGroupManageRequest) => {
    if (!adminApiKey) return;
    const result = await dispatch(updateGroupThunk({ id, payload, adminApiKey }));
    if (updateGroupThunk.fulfilled.match(result)) {
      setFormOpen(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!adminApiKey || !deleteTarget) return;
    await dispatch(deleteGroupThunk({ id: deleteTarget.id, adminApiKey }));
    setDeleteTarget(null);
  };

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Client Groups</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button startIcon={<Refresh />} onClick={handleRefresh} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
              Add Client Group
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearGroupError())}>
            {error}
          </Alert>
        )}
        {actionMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(clearGroupMessage())}>
            {actionMessage}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Client ID</TableCell>
                  <TableCell>Client Secret</TableCell>
                  <TableCell align="center">OAuth (v2)</TableCell>
                  <TableCell align="center">Devices</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.clientId}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {group.clientSecret}
                    </TableCell>
                    <TableCell align="center">
                      {group.requiresOAuth ? (
                        <Chip size="small" color="success" label="Yes" variant="outlined" />
                      ) : (
                        <Chip size="small" color="default" label="No" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="center">{group.deviceCount}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(group)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={group.deviceCount > 0 ? 'Cannot delete: devices assigned' : 'Delete'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteTarget(group)}
                            disabled={group.deviceCount > 0}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No client groups found. Click "Add Client Group" to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <ClientGroupFormDialog
        open={formOpen}
        mode={formMode}
        group={editingGroup}
        saving={saving}
        onClose={() => setFormOpen(false)}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete client group?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete client group <strong>{deleteTarget?.name}</strong> (
            {deleteTarget?.clientId})? Groups with assigned devices cannot be deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained" disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientGroupManagement;
